import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";
import type { ManufacturerRatingKind } from "@/lib/manufacturer-ratings";

// Sales' ratings of each manufacturer per store category: Good / Better / Best (USA-1258),
// Preference and Pricing (USA-1272).
export type ManufacturerQualityTier = components["schemas"]["ManufacturerQualityTierRead"];
export type QualityTierPeer = components["schemas"]["QualityTierPeerRead"];
export type QualityTierCategory = components["schemas"]["QualityTierCategoryRead"];
export type QualityTierCategoryDetail = components["schemas"]["QualityTierCategoryDetailRead"];
export type QualityTier = NonNullable<ManufacturerQualityTier["quality_tier"]>;

export const QUALITY_TIER_LABELS: Record<QualityTier, string> = {
  good: "Good",
  better: "Better",
  best: "Best",
};
// Best first, for lists that compare manufacturers.
export const QUALITY_TIERS_BEST_FIRST: QualityTier[] = ["best", "better", "good"];

export function useManufacturerQualityTiers(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.manufacturers.qualityTiers(manufacturerUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/manufacturers/{uuid}/quality-tiers", {
        params: { path: { uuid: manufacturerUuid } },
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useQualityTierCategories() {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.qualityTiers.categories,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/quality-tiers/categories");
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

export function useQualityTierCategory(nodeId: number | null) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: queryKeys.qualityTiers.category(nodeId ?? 0),
    enabled: nodeId != null,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/quality-tiers/categories/{taxonomy_node_id}", {
        params: { path: { taxonomy_node_id: nodeId ?? 0 } },
      });
      if (error) throw error;
      return data;
    },
  });
}

type RatingFields = { own: string; effective: string; from: string }
// Where each rating sits on a category row.
const CATEGORY_FIELDS: Record<ManufacturerRatingKind, RatingFields> = {
  quality_tier: { own: "quality_tier", effective: "effective_tier", from: "inherited_from" },
  preference: { own: "preference", effective: "effective_preference", from: "preference_inherited_from" },
  pricing: { own: "pricing", effective: "effective_pricing", from: "pricing_inherited_from" },
};

export type RatingChange = {
  manufacturerUuid: string;
  nodeId: number;
  rating: ManufacturerRatingKind;
  value: string | null;
};

/** Set or clear one rating of a manufacturer in one category; both rating views update right away. */
export function useSetRating() {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ manufacturerUuid, nodeId, rating, value }: RatingChange) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/manufacturers/{uuid}/quality-tiers/{taxonomy_node_id}",
        {
          params: { path: { uuid: manufacturerUuid, taxonomy_node_id: nodeId } },
          body: { [rating]: value } as components["schemas"]["ManufacturerQualityTierUpdate"],
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ manufacturerUuid, nodeId, rating, value }) => {
      const manufacturerKey = queryKeys.manufacturers.qualityTiers(manufacturerUuid);
      const categoryKey = queryKeys.qualityTiers.category(nodeId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: manufacturerKey }),
        queryClient.cancelQueries({ queryKey: categoryKey }),
      ]);
      const previousManufacturer = queryClient.getQueryData<ManufacturerQualityTier[]>(manufacturerKey);
      const previousCategory = queryClient.getQueryData<QualityTierCategoryDetail>(categoryKey);
      queryClient.setQueryData<ManufacturerQualityTier[]>(manufacturerKey, (old) =>
        old?.map((row) => (row.taxonomy_node_id === nodeId ? { ...row, [rating]: value } : row)),
      );
      const fields = CATEGORY_FIELDS[rating];
      queryClient.setQueryData<QualityTierCategoryDetail>(categoryKey, (old) =>
        old
          ? {
              ...old,
              manufacturer_tiers: (old.manufacturer_tiers ?? []).map((row) => {
                if (row.manufacturer_uuid !== manufacturerUuid) return row;
                const current = row as Record<string, unknown>;
                return {
                  ...row,
                  [fields.own]: value,
                  [fields.effective]: value ?? (current[fields.from] ? current[fields.effective] : null),
                };
              }),
            }
          : old,
      );
      return { manufacturerKey, categoryKey, previousManufacturer, previousCategory };
    },
    onError: (_error, _vars, context) => {
      if (!context) return;
      queryClient.setQueryData(context.manufacturerKey, context.previousManufacturer);
      queryClient.setQueryData(context.categoryKey, context.previousCategory);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.qualityTiers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.allQualityTiers });
    },
  });
}

// ---- Comments on a manufacturer's ratings in one category (USA-1272) ----

export type RatingComment = components["schemas"]["ManufacturerRatingCommentRead"];
export type RatingThread = { nodeId: number; manufacturerUuid: string };

const threadPath = ({ nodeId, manufacturerUuid }: RatingThread) => ({
  taxonomy_node_id: nodeId,
  manufacturer_uuid: manufacturerUuid,
});
const threadKey = ({ nodeId, manufacturerUuid }: RatingThread) =>
  queryKeys.qualityTiers.comments(nodeId, manufacturerUuid);

export function useRatingComments(thread: RatingThread) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: threadKey(thread),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/quality-tiers/categories/{taxonomy_node_id}/manufacturers/{manufacturer_uuid}/comments",
        { params: { path: threadPath(thread) } },
      );
      if (error) throw error;
      return data;
    },
  });
}

/** After a comment is added or removed: the thread and the comment counts on both rating views. */
function useRefreshCounts(thread: RatingThread) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: threadKey(thread) });
    queryClient.invalidateQueries({ queryKey: queryKeys.qualityTiers.category(thread.nodeId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.qualityTiers(thread.manufacturerUuid) });
  };
}

export function useCreateRatingComment(thread: RatingThread) {
  const getClient = useAuthenticatedClient();
  const refresh = useRefreshCounts(thread);
  return useMutation({
    mutationFn: async (body: components["schemas"]["ManufacturerRatingCommentCreate"]) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/quality-tiers/categories/{taxonomy_node_id}/manufacturers/{manufacturer_uuid}/comments",
        { params: { path: threadPath(thread) }, body },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: refresh,
  });
}

export function useUpdateRatingComment(thread: RatingThread) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const listKey = threadKey(thread);
  return useMutation({
    mutationFn: async ({ commentUuid, content }: { commentUuid: string; content: string }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH("/dashboard/quality-tiers/comments/{comment_uuid}", {
        params: { path: { comment_uuid: commentUuid } },
        body: { content },
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ commentUuid, content }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<RatingComment[]>(listKey);
      queryClient.setQueryData<RatingComment[]>(listKey, (old) =>
        old?.map((comment) =>
          comment.uuid === commentUuid ? { ...comment, content, updated_at: new Date().toISOString() } : comment,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });
}

export function useDeleteRatingComment(thread: RatingThread) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const refresh = useRefreshCounts(thread);
  const listKey = threadKey(thread);
  return useMutation({
    mutationFn: async (commentUuid: string) => {
      const client = await getClient();
      const { error } = await client.DELETE("/dashboard/quality-tiers/comments/{comment_uuid}", {
        params: { path: { comment_uuid: commentUuid } },
      });
      if (error) throw error;
    },
    onMutate: async (commentUuid) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<RatingComment[]>(listKey);
      queryClient.setQueryData<RatingComment[]>(listKey, (old) =>
        old?.filter((comment) => comment.uuid !== commentUuid),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(listKey, context.previous);
    },
    onSettled: refresh,
  });
}
